import { type AiAgentId } from '@fruitful/llm-service-shared';
import { type ReactNode, useMemo, useState } from 'react';
import { BadgeLabel, type BadgeLabelVariant } from 'smoothie';
import { BodySmallRegular, ScrollView, XStack, YStack } from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import {
    type RawThreadMessage,
    useGetMastraThreadRawMessages,
} from '../hooks/useGetMastraThreadRawMessages';
import { useLiveThreadRefetchInterval } from '../hooks/useLiveThreadRefetchInterval';

/** Long string values (system prompts, skill markdown, tool results) are
 * collapsed to this many characters until the block is expanded. */
const MAX_STRING_LEN = 400;

/** Mastra's built-in workspace skill tools (see `createSkillTools`). */
const SKILL_TOOL_NAMES = new Set(['skill', 'skill_search', 'skill_read']);

const MONO_STYLE = {
    fontFamily: 'monospace',
    fontSize: 11.5,
    lineHeight: 17,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
} as const;

interface RawPart {
    type: string;
    [key: string]: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const asString = (value: unknown): string | undefined =>
    typeof value === 'string' ? value : undefined;

const sortByCreatedAt = (messages: RawThreadMessage[]): RawThreadMessage[] =>
    [...messages].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

const roleBadgeVariant = (role: string): BadgeLabelVariant => {
    switch (role) {
        case 'assistant':
            return 'positive';
        case 'user':
            return 'blue';
        case 'tool':
            return 'marigold';
        default:
            return 'gray';
    }
};

/** Pull the `parts` array out of the shapes Mastra memory returns. */
const extractParts = (message: unknown): RawPart[] | null => {
    if (!isRecord(message)) {
        return null;
    }
    if (Array.isArray(message.parts)) {
        return message.parts.filter(isRecord) as RawPart[];
    }
    if (isRecord(message.content) && Array.isArray(message.content.parts)) {
        return message.content.parts.filter(isRecord) as RawPart[];
    }
    return null;
};

const isToolPart = (part: RawPart): boolean =>
    part.type === 'dynamic-tool' || part.type.startsWith('tool-');

interface PartDescriptor {
    label: string;
    detail?: string;
    variant: BadgeLabelVariant;
}

const describePart = (part: RawPart): PartDescriptor => {
    if (isToolPart(part)) {
        const invocation = isRecord(part.toolInvocation)
            ? part.toolInvocation
            : part;
        const toolName =
            asString(invocation.toolName) ??
            (part.type !== 'tool-invocation' && part.type !== 'dynamic-tool'
                ? part.type.slice('tool-'.length)
                : undefined) ??
            'unknown';
        const isSkill = SKILL_TOOL_NAMES.has(toolName);
        const state = asString(invocation.state);
        return {
            label: isSkill ? 'skill' : 'tool',
            detail: state != null ? `${toolName} · ${state}` : toolName,
            variant: isSkill ? 'marigold' : 'blue',
        };
    }

    switch (part.type) {
        case 'text':
            return { label: 'text', variant: 'gray' };
        case 'reasoning':
            return { label: 'reasoning', variant: 'gray' };
        case 'step-start':
            return { label: 'step-start', variant: 'gray' };
        case 'data-ui':
            return {
                label: 'widget',
                detail: isRecord(part.data)
                    ? asString(part.data.widget)
                    : undefined,
                variant: 'green',
            };
        default:
            return { label: part.type, variant: 'neutral' };
    }
};

/** JSON replacer that clips long string values so a giant prompt or skill
 * result doesn't blow up the card. */
const truncateLongStrings = (_key: string, value: unknown): unknown =>
    typeof value === 'string' && value.length > MAX_STRING_LEN
        ? `${value.slice(0, MAX_STRING_LEN)}… [+${(
              value.length - MAX_STRING_LEN
          ).toLocaleString()} chars]`
        : value;

const stringify = (value: unknown, truncate: boolean): string => {
    try {
        return JSON.stringify(
            value,
            truncate ? truncateLongStrings : undefined,
            2,
        );
    } catch {
        return String(value);
    }
};

const formatCreatedAt = (createdAt: Date): string =>
    createdAt instanceof Date ? createdAt.toISOString() : String(createdAt);

/** Pretty-printed JSON with inline truncation of long strings and its own
 * expand/collapse toggle. */
const useCollapsibleJson = (value: unknown) => {
    const [expanded, setExpanded] = useState(false);
    const full = useMemo(() => stringify(value, false), [value]);
    const truncated = useMemo(() => stringify(value, true), [value]);
    const canExpand = full !== truncated;
    return {
        body: expanded || !canExpand ? full : truncated,
        charCount: full.length,
        canExpand,
        expanded,
        toggle: () => {
            setExpanded((prev) => !prev);
        },
    };
};

const ToggleButton = ({
    expanded,
    onToggle,
}: {
    expanded: boolean;
    onToggle: () => void;
}) => (
    <Button variant="tertiary" size="tiny" onPress={onToggle}>
        {expanded ? 'Collapse' : 'Show full JSON'}
    </Button>
);

const MonoMeta = ({
    children,
    color = '$gray700',
    size = 11,
}: {
    children: ReactNode;
    color?: string;
    size?: number;
}) => (
    <BodySmallRegular
        color={color}
        style={{ fontFamily: 'monospace', fontSize: size }}
    >
        {children}
    </BodySmallRegular>
);

/** One decomposed part of an assistant message (tool call, skill call, text,
 * reasoning…) rendered as a nested card with its own raw JSON. */
const PartCard = ({ part, index }: { part: RawPart; index: number }) => {
    const descriptor = describePart(part);
    const json = useCollapsibleJson(part);

    return (
        <YStack
            borderWidth={1}
            borderColor="$gray300"
            borderRadius="$2"
            overflow="hidden"
            backgroundColor="$gray200"
        >
            <XStack
                gap="$2"
                alignItems="center"
                flexWrap="wrap"
                paddingHorizontal="$2"
                paddingVertical="$1.5"
                borderBottomWidth={1}
                borderBottomColor="$gray300"
            >
                <MonoMeta>{index}</MonoMeta>
                <BadgeLabel
                    variant={descriptor.variant}
                    text={descriptor.label}
                />
                {descriptor.detail != null ? (
                    <MonoMeta color="$gray1100">{descriptor.detail}</MonoMeta>
                ) : null}
                <MonoMeta color="$gray600">
                    {json.charCount.toLocaleString()} chars
                </MonoMeta>
                {json.canExpand ? (
                    <XStack flex={1} justifyContent="flex-end">
                        <ToggleButton
                            expanded={json.expanded}
                            onToggle={json.toggle}
                        />
                    </XStack>
                ) : null}
            </XStack>
            <YStack paddingHorizontal="$2" paddingVertical="$1.5">
                <BodySmallRegular color="$gray1200" style={MONO_STYLE}>
                    {json.body}
                </BodySmallRegular>
            </YStack>
        </YStack>
    );
};

/** A single raw message: header chrome, then either a decomposed list of part
 * cards (assistant turns with tool/skill calls) or one raw JSON blob. */
const RawMessageCard = ({
    message,
    index,
}: {
    message: RawThreadMessage;
    index: number;
}) => {
    const json = useCollapsibleJson(message.message);
    const parts = extractParts(message.message);
    const decompose =
        parts != null && (parts.length > 1 || parts.some(isToolPart));

    return (
        <YStack
            borderWidth={1}
            borderColor="$gray300"
            borderRadius="$3"
            overflow="hidden"
            backgroundColor="$gray100"
        >
            <XStack
                gap="$2"
                alignItems="center"
                flexWrap="wrap"
                paddingHorizontal="$3"
                paddingVertical="$2"
                borderBottomWidth={1}
                borderBottomColor="$gray300"
            >
                <MonoMeta size={12}>#{index}</MonoMeta>
                <BadgeLabel
                    variant={roleBadgeVariant(message.role)}
                    text={message.role}
                />
                <MonoMeta>{formatCreatedAt(message.createdAt)}</MonoMeta>
                <MonoMeta color="$gray600">
                    {json.charCount.toLocaleString()} chars
                </MonoMeta>
                {decompose ? (
                    <MonoMeta color="$gray600">
                        {parts.length} part{parts.length === 1 ? '' : 's'}
                    </MonoMeta>
                ) : null}
                {!decompose && json.canExpand ? (
                    <XStack flex={1} justifyContent="flex-end">
                        <ToggleButton
                            expanded={json.expanded}
                            onToggle={json.toggle}
                        />
                    </XStack>
                ) : null}
            </XStack>
            <YStack gap="$2" paddingHorizontal="$3" paddingVertical="$2">
                {decompose ? (
                    parts.map((part, partIndex) => (
                        <PartCard
                            key={partIndex}
                            part={part}
                            index={partIndex}
                        />
                    ))
                ) : (
                    <BodySmallRegular color="$gray1200" style={MONO_STYLE}>
                        {json.body}
                    </BodySmallRegular>
                )}
            </YStack>
        </YStack>
    );
};

export const AiThreadRawPanel = ({
    threadId,
    agentId,
}: {
    threadId: string | undefined;
    agentId: AiAgentId;
}) => {
    const refetchInterval = useLiveThreadRefetchInterval();

    const {
        data: messages,
        isLoading,
        error,
    } = useGetMastraThreadRawMessages({
        threadId,
        agentId,
        refetchInterval,
    });

    const rawMessages = messages != null ? sortByCreatedAt(messages) : [];

    return (
        <YStack flex={1} minHeight="70vh" width="100%" overflow="hidden">
            <ScrollView flex={1}>
                <YStack gap="$2" padding="$4">
                    {rawMessages.length > 0 ? (
                        <>
                            <MonoMeta>
                                {rawMessages.length} raw message
                                {rawMessages.length === 1 ? '' : 's'} — exact
                                Mastra memory records
                            </MonoMeta>
                            {rawMessages.map((message, index) => (
                                <RawMessageCard
                                    key={message.id}
                                    message={message}
                                    index={index}
                                />
                            ))}
                        </>
                    ) : isLoading ? (
                        <BodySmallRegular color="$gray700">
                            Loading thread...
                        </BodySmallRegular>
                    ) : error != null ? (
                        <BodySmallRegular color="$red900">
                            Unable to load conversation transcript.
                        </BodySmallRegular>
                    ) : (
                        <BodySmallRegular color="$gray700">
                            No messages found for thread.
                        </BodySmallRegular>
                    )}
                </YStack>
            </ScrollView>
        </YStack>
    );
};
